import React from "react";

const article = {
  slug: "how-often-should-you-service-a-plc",
  title: "How often should you service a PLC? A practical UK guide",
  dek: "PLCs are often the least-maintained asset on a modern production line — because they seem to just work. Here's what \"servicing\" a PLC actually means, how often you should be doing it, and the signs your controller needs attention right now.",
  category: "Controls & Automation",
  readTime: "8 min read",
  publishedAt: "2026-02-05",
  image: "/gallery/db-panel-2.jpg",
  imagePosition: "center 45%",
  relatedServiceSlugs: [
    "plc-programming",
    "hmi-and-scada-development",
    "industrial-automation",
    "planned-preventive-maintenance",
  ],
  faqs: [
    {
      q: "PLCs don't have moving parts — why service them at all?",
      a: "The processor doesn't wear, but the environment around it does. Batteries lose charge, memory cards degrade, terminal connections loosen, cooling fans clog, cabinet seals fail, and the software running on the PLC drifts as ad-hoc changes accumulate. A PLC service is really about the surrounding installation and the discipline around the program — not the CPU itself.",
    },
    {
      q: "How often should we service a critical PLC?",
      a: "Interval depends on risk. A common baseline for a production-critical PLC is a quarterly light-touch check (visual, environment, comms health) and an annual deep service (battery, backup verification, program comparison, terminal torque check, thermographic scan). Non-critical PLCs may only justify an annual visit. The key is that intervals are risk-based, not arbitrary.",
    },
    {
      q: "What are the signs a PLC needs urgent attention?",
      a: "Recurring unexplained trips, sporadic comms drop-outs to HMIs or drives, sluggish scan times, a battery warning light on the CPU, warm/hot terminations found by thermal imaging, or the sinking feeling that no one on-site knows exactly what version of the program is running. Any of these warrants an unplanned inspection, not a delayed one.",
    },
    {
      q: "Do we need to replace a PLC just because it's old?",
      a: "Not automatically. Age alone isn't a fault — a well-installed PLC can run reliably for many years. What forces replacement is usually obsolescence: parts no longer available, no vendor support, incompatibility with newer HMI or SCADA platforms, or a program written in a language nobody on the team can maintain. Plan for obsolescence, don't be surprised by it.",
    },
  ],
  body: (
    <>
      <p>
        PLCs are, quietly, the most trusted piece of kit in a modern factory.
        They sit in the panel, blink patiently, and rarely ask for anything.
        That&apos;s exactly the problem. Because they don&apos;t announce
        themselves — no belt to break, no seal to leak — they end up at the
        very bottom of the PPM list. Then, one Sunday night, something drops,
        no one has the program, and the works manager has an interesting Monday.
      </p>
      <p>
        This article is about setting a sensible, risk-based service interval
        for the PLCs on your site, and about what &quot;servicing&quot; a PLC
        actually looks like in practice. Nothing exotic — just the discipline
        that separates a controller that runs for fifteen years from one that
        drops out at year seven.
      </p>

      <h2>What &quot;servicing a PLC&quot; actually means</h2>
      <p>
        A PLC service is not usually about the CPU itself. Modern industrial
        processors are extremely reliable. What is being maintained is the
        installation around the PLC and the software running on it. A
        reasonable service covers six areas:
      </p>
      <ul>
        <li>
          <strong>Battery.</strong> Where the platform still uses a backup
          battery for retentive memory or the real-time clock, check the
          voltage and status flag and replace on the manufacturer&apos;s
          recommended cycle — typically several years but earlier if any
          low-battery warning has been logged.
        </li>
        <li>
          <strong>Firmware and memory card.</strong> Verify firmware version
          against the vendor&apos;s supported baseline. Read the memory card
          into the engineering workstation and confirm it matches the master
          program on file. If it doesn&apos;t match, someone has changed the
          program online without updating the master — and you need to know.
        </li>
        <li>
          <strong>Program backups.</strong> Take a fresh backup of the running
          program, comments and symbols. Test the restore on a spare CPU or in
          the vendor&apos;s emulator. An untested backup is not a backup.
        </li>
        <li>
          <strong>I/O terminations.</strong> Torque check terminal blocks and
          IO cards where safe to do so. Loose terminations are a leading cause
          of intermittent, hard-to-find faults on live systems. Thermal
          imaging of the panel is the safer non-invasive equivalent.
        </li>
        <li>
          <strong>Communications.</strong> Check network health for the PLC&apos;s
          buses — Ethernet/IP, Profinet, Modbus TCP, Modbus RTU, CANopen or
          whatever the platform uses. Look for CRC errors, dropped frames,
          duplicate IP addresses, and drift on subordinate device counts.
        </li>
        <li>
          <strong>Environment.</strong> Panel cooling fans and filters clean
          and running. Cabinet seals intact. Ambient temperature and humidity
          inside the range on the CPU datasheet. Dust — especially conductive
          or damp dust — cleaned out of the panel with the appropriate
          method for a live cabinet.
        </li>
      </ul>

      <h2>Setting a sensible interval</h2>
      <p>
        There is no single &quot;right&quot; frequency for servicing a PLC — the
        Electricity at Work Regulations 1989 impose a maintenance duty
        without prescribing intervals, and vendor recommendations vary by
        product. Interval should be set based on the risk the controller
        carries: how critical is what it&apos;s doing, and how bad is a stop?
      </p>
      <p>A workable framework for most sites looks like this:</p>
      <ul>
        <li>
          <strong>Critical PLC</strong> (production line, safety interlock,
          batching, refrigeration): quarterly light-touch (visual, comms
          health, HMI walkthrough, thermographic scan of panel) plus an
          annual deep service.
        </li>
        <li>
          <strong>Important PLC</strong> (utility plant, back-of-house
          services): annual deep service plus a mid-year non-invasive check.
        </li>
        <li>
          <strong>Standalone / low-consequence PLC</strong> (single-machine
          controllers, ancillary equipment): annual visit at the same time as
          the machine itself is serviced.
        </li>
      </ul>
      <p>
        Whichever cadence you pick, write it into the site PPM plan alongside
        the other assets — don&apos;t treat controllers as an
        electrical-department afterthought.
      </p>

      <h2>Signs your PLC needs unplanned attention</h2>
      <p>
        Between planned services, keep an eye out for the symptoms that
        precede a serious failure. Any of the following justifies pulling the
        interval forward:
      </p>
      <ul>
        <li>Recurring unexplained trips or resets — especially at consistent times of day</li>
        <li>HMIs losing comms to the PLC intermittently</li>
        <li>Sluggish or drifting scan times</li>
        <li>Any battery warning LED or diagnostic bit set</li>
        <li>Hot terminations or panel components on a thermographic sweep</li>
        <li>Nobody on site is confident what version of the program is running</li>
        <li>Changes have been made online without an updated master backup</li>
      </ul>
      <p>
        The last two are software issues rather than hardware ones, but they
        cause the most expensive Sundays.
      </p>

      <h2>Program governance — the least glamorous, biggest win</h2>
      <p>
        Most of the pain we see with older PLCs on client sites is not the
        hardware — it&apos;s that the program has drifted from any recognisable
        master. Online tweaks made during a breakdown never made it back into
        source control. Comments have been stripped. Symbol names are out of
        date. Nobody remembers why a bit is forced.
      </p>
      <p>Two simple governance rules stop this in its tracks:</p>
      <ol>
        <li>
          Every change — however small, however urgent — is committed to the
          source of truth within 24 hours of the visit. The source of truth
          can be a shared drive, a version-controlled repository or the
          Operations Portal — but there is one, and it is the one everyone
          trusts.
        </li>
        <li>
          At every service visit, the running program on the PLC is compared
          against the source-of-truth master. Any deltas are investigated and
          either promoted to the master or backed out. No exceptions.
        </li>
      </ol>

      <h2>Obsolescence — plan for it, don&apos;t be surprised by it</h2>
      <p>
        A PLC platform typically enjoys a long support life, but nothing runs
        forever. When the vendor announces end-of-life, work with your
        controls engineer to build a migration plan on a three- to five-year
        horizon: parts availability, program conversion effort, downtime
        window, and — often overlooked — the state of the surrounding I/O and
        wiring. Retrofitting a modern CPU into a panel where half the
        terminations are original 1990s work is rarely a straight swap.
      </p>

      <h2>The takeaway</h2>
      <p>
        A PLC that&apos;s been serviced — hardware, firmware and program —
        gives you predictable behaviour year after year. A PLC that has been
        left alone will eventually fail in the way you least want it to.
        Put the interval in the PPM plan, put the program in one trusted
        place, and treat the controller with the same discipline you already
        apply to the motors and pumps around it.
      </p>

      <h2>A short checklist for the next service visit</h2>
      <p>
        If you&apos;re booking a PLC service and want a workable brief to
        send to the engineer, the list below is a solid starting point. It
        covers the essentials without adding busy-work.
      </p>
      <ol>
        <li>
          Take a fresh source-code and configuration backup <em>before</em>{" "}
          touching anything. Verify the file opens in the engineering tool.
        </li>
        <li>
          Compare the running program against the source-of-truth master.
          Investigate and document any differences.
        </li>
        <li>
          Read the CPU diagnostic buffer / fault log. Address anything
          logged since the last visit.
        </li>
        <li>
          Check firmware version against the vendor&apos;s supported
          baseline; escalate if any component is out of support.
        </li>
        <li>
          Inspect the battery and memory card status; replace on
          manufacturer schedule.
        </li>
        <li>
          Torque-check accessible terminations on the PLC and remote I/O
          racks; thermographic scan of the panel where safe to do so.
        </li>
        <li>
          Verify comms health — port statistics, error counters, subordinate
          device counts — for every network the controller is on.
        </li>
        <li>
          Test panel cooling: filters clean, fans operating, ambient
          temperature within CPU datasheet limits.
        </li>
        <li>
          Walk through the HMI: every screen, every alarm, every trend.
          Confirm nothing is showing a permanent warning that&apos;s become
          &quot;normal&quot;.
        </li>
        <li>
          Update the master backup, tag it with date, version and engineer,
          and store it in the agreed source of truth. Log the visit.
        </li>
      </ol>
    </>
  ),
};

export default article;
